// src/pages/CollectorPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import RecordDetails from '../components/RecordDetails.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import HistoryTab from '../components/HistoryTab.jsx';
import Header from '../components/Header.jsx';
import SummaryTab from '../components/SummaryTab.jsx';
import NotificationsTab from '../components/NotificationsTab.jsx';
import Pagination from '../components/Pagination.jsx';
import { Bell, ClipboardList, Clock, History, RefreshCw, BarChart } from 'lucide-react';
// We don't need to import the modal here, Header handles it.

const CollectorPage = () => {
  // Use 'localUser' to track profile changes, initialized from 'authedUser'
  const { user: authedUser, logout, loading: authLoading } = useAuth();
  const [user, setUser] = useState(authedUser); // Local state for user
  const navigate = useNavigate();

  // Records tab (active)
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // History tab
  const [historyRecords, setHistoryRecords] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(25);

  // Shared UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewMode, setViewMode] = useState('records');
  const [searchTerm, setSearchTerm] = useState('');

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState('');

  // --- NEW ---
  // When the authedUser from context changes (e.g., on login), update local user state
  useEffect(() => {
    setUser(authedUser);
  }, [authedUser]);

  // --- NEW ---
  // This function will be called by the modal on a successful update
  const handleUpdateUser = (updatedUserData) => {
    // Update the local user state, which will re-render Header with new name
    setUser(updatedUserData); 
    // We assume the auth context will also eventually refresh,
    // but this gives instant UI feedback.
  };

  const fetchNotifications = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const res = await api.get('/records/notifications');
      setNotifications(res.data || []);
      setNotificationsError('');
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setNotificationsError('Failed to fetch notifications.');
    } finally {
      setNotificationsLoading(false);
    }
  }, [user]); // Depends on local user state

  // --- PAGINATED FETCHES ---
  const fetchActive = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/records', {
        params: {
          collectorId: user.userId,
          page,
          limit,
          search: searchTerm.trim() || undefined,
          category: 'active',
        },
      });
      const { data, total: t } = res.data || {};
      setRecords(data || []);
      setTotal(t || 0);
    } catch (err) {
      console.error('Error fetching active records:', err);
      const msg = err.response?.data?.message || err.message;
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setError(`Failed to fetch your assigned records: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  }, [user, page, limit, searchTerm, logout, navigate]); // Depends on local user state

  const fetchHistory = useCallback(async () => {
    if (!user?.userId) return;
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/records', {
        params: {
          collectorId: user.userId,
          page: historyPage,
          limit: historyLimit,
          search: searchTerm.trim() || undefined,
          category: 'history',
        },
      });
      const { data, total: t } = res.data || {};
      setHistoryRecords(data || []);
      setHistoryTotal(t || 0);
    } catch (err) {
      console.error('Error fetching history records:', err);
      setError('Failed to fetch history.');
    } finally {
      setLoading(false);
    }
  }, [user, historyPage, historyLimit, searchTerm]); // Depends on local user state

  // Notifications polling
  useEffect(() => {
    if (!authLoading && user?.userId) {
      fetchNotifications();
      const id = setInterval(fetchNotifications, 60000);
      return () => clearInterval(id);
    }
  }, [authLoading, user, fetchNotifications]); // Depends on local user state

  // Initial route/guard
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, navigate]); // Depends on local user state

  // React to page/limit changes
  useEffect(() => {
    if (!authLoading && user?.userId && !selectedRecord && viewMode === 'records') {
      fetchActive();
    }
  }, [authLoading, user, selectedRecord, page, limit, viewMode, fetchActive]); // Depends on local user state

  useEffect(() => {
    if (!authLoading && user?.userId && !selectedRecord && viewMode === 'history') {
      fetchHistory();
    }
  }, [authLoading, user, selectedRecord, historyPage, historyLimit, viewMode, fetchHistory]); // Depends on local user state

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => {
      if (viewMode === 'records') {
        setPage(1);
      } else if (viewMode === 'history') {
        setHistoryPage(1);
      }
    }, 300);
    return () => clearTimeout(id);
  }, [searchTerm, viewMode]); 

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveRecord = async (id, data) => {
    try {
      const { assignedCollector, comments, _id, __v, ...payload } = data;
      const res = await api.put(`/records/${id}`, payload);
      const updated = res.data;
      setSelectedRecord(updated); 
      if (viewMode === 'records') fetchActive();
      if (viewMode === 'history') fetchHistory();
      return updated;
    } catch (err) {
      console.error(err);
      setError('Failed to update record.');
      throw err;
    }
  };

  const handleNotificationSelect = async id => {
    try {
      const res = await api.get(`/records/${id}`);
      setSelectedRecord(res.data);
    } catch (err) {
      console.error('Failed to fetch record from notification:', err);
      setError('Could not load the selected record.');
    }
  };

  if (authLoading) return <div className="text-center p-8">Loading authentication...</div>;
  if (!user) return <div className="text-center p-8">Redirecting to login...</div>;

  const navItems = [
    { label: 'My Records', icon: <ClipboardList className="w-4 h-4" />, mode: 'records' },
    { label: 'History', icon: <History className="w-4 h-4" />, mode: 'history' },
    { label: 'Schedule', icon: <Clock className="w-4 h-4" />, mode: 'schedule' },
    { label: 'Alerts', icon: <Bell className="w-4 h-4" />, mode: 'notifications' },
    { label: 'Summary', icon: <BarChart className="w-4 h-4" />, mode: 'summary' }, 
  ];

  return (
    <>
      <Header
        user={user} // Pass local user state
        onLogout={handleLogout}
        api={api} // --- PASS API PROP ---
        onUpdateUser={handleUpdateUser} // --- PASS UPDATE HANDLER ---
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={setViewMode}
        overdueTaskCount={notifications.length}
      />

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          
          {/* --- NEW CONDITIONAL WRAPPER --- */}
          {selectedRecord ? (
            <RecordDetails
              record={selectedRecord}
              onBack={() => {
                setSelectedRecord(null);
                if (viewMode === 'records') fetchActive();
                if (viewMode === 'history') fetchHistory();
              }}
              onSave={handleSaveRecord}
              user={user} // Pass local user state
              api={api}
            />
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 rounded-xl border-l-4 bg-red-50 border-red-500 text-red-800 ">
                  {error}
                </div>
              )}

              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {viewMode === 'records' && (
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                      <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                        My Assigned Records
                      </h2>
                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          placeholder="Search by Provider, Patient, Adj, lien status…"
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          className="px-8 py-2 w-full sm:w-96 md:w-[448px] lg:w-[480px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                        <button
                          onClick={fetchActive}
                          disabled={loading}
                          className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition disabled:bg-gray-400"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </button>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-8 text-gray-500">Loading...</div>
                    ) : records.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adj Number</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bill</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Case Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lien Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {records.map(r => (
                                <tr key={r._id} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">{r.provider}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.ptName}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.adjNumber?.[0]?.value || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {typeof r.bill === 'number' ? `$${r.bill.toLocaleString()}` : 'N/A'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.caseStatus || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">{r.lienStatus || 'N/A'}</td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                      onClick={() => setSelectedRecord(r)}
                                      className="text-indigo-600 hover:text-indigo-900 font-medium"
                                    >
                                      Details
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <Pagination
                          page={page}
                          setPage={setPage}
                          limit={limit}
                          setLimit={setLimit}
                          total={total}
                        />
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        {searchTerm ? 'No records match your search.' : 'No new records assigned to you.'}
                      </div>
                    )}
                  </div>
                )}

                {viewMode === 'history' && (
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                      <h2 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">History</h2>
                      <input
                        type="text"
                        placeholder="Search history…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="px-8 py-2 w-full sm:w-96 md:w-[448px] lg:w-[480px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      />
                    </div>

                    <HistoryTab
                      records={historyRecords}
                      onRecordSelect={setSelectedRecord}
                      loading={loading}
                      user={user} // Pass local user state
                    />

                    <Pagination
                      page={historyPage}
                      setPage={setHistoryPage}
                      limit={historyLimit}
                      setLimit={setHistoryLimit}
                      total={historyTotal}
                    />
                  </div>
                )}

                {viewMode === 'schedule' && (
                  <div className="p-6">
                    <ScheduleCalendar user={user} onRecordSelect={setSelectedRecord} api={api} />
                  </div>
                )}

                {viewMode === 'notifications' && (
                  <div className="p-6">
                    <NotificationsTab
                      notifications={notifications}
                      onRecordSelect={handleNotificationSelect}
                      loading={notificationsLoading}
                      error={notificationsError}
                    />
                  </div>
                )}
                {viewMode === 'summary' && (
                  <div className="p-6">
                    <SummaryTab api={api} />
                  </div>
              )}
              </div>
            </>
          )}
          {/* --- END CONDITIONAL WRAPPER --- */}
        </div>
      </div>
    </>
  );
};

export default CollectorPage;

